import React, { Suspense, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import PageConfig from '@/component/layout/PageConfig.tsx';
import { useSearchParams } from 'react-router-dom';
import { GoogleMap, MarkerF, InfoWindowF, CircleF, PolylineF } from '@react-google-maps/api';
import { IMarker, IMarkerInfo, MarketTYPE } from '@/pages/dashboard/DashboardMapDefault.tsx';
import { AlertStatus } from '@/api/user/getUsers.ts';
import useIsMount from '@/util/hook/useIsMount.ts';
import ReactDOMServer from 'react-dom/server';
import MarkerWindow from '@/pages/dashboard/MarkerWindow.tsx';
import HomeIcon from '@/assets/ic_map_house.svg?react';
import useQueryGetUserDailyPosition, { IUserPosition, apiGetUserDailyPosition } from '@/api/user/getUserDailyPosition.ts';
import useQueryGetUsers from '@/api/user/getUsers.ts';
import useQueryGetUserDetail from '@/api/auth/getUser.ts';
import { format, isToday, isBefore, startOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';

export interface IProps {}

// 날짜별 데이터 존재 여부 인터페이스
interface IDateAvailability {
    date: string;
    hasData: boolean;
    isLoading: boolean;
}

// 커스텀 달력 컴포넌트
const CustomDatePicker: React.FC<{
    value: string;
    onChange: (date: string) => void;
    availableDates: IDateAvailability[];
    isLoadingAvailability: boolean;
    onMonthChange: (month: Date) => void;
}> = ({ value, onChange, availableDates, isLoadingAvailability, onMonthChange }) => {
    const { t } = useTranslation();
    const [showCalendar, setShowCalendar] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const buttonRef = useRef<HTMLButtonElement>(null);

    // 월이 변경될 때마다 부모 컴포넌트에 알림
    useEffect(() => {
        onMonthChange(currentMonth);
    }, [currentMonth, onMonthChange]);

    // 해당 날짜에 데이터가 있는지 확인
    const isDateAvailable = (date: string) => {
        const availability = availableDates.find(item => item.date === date);
        return availability?.hasData || false;
    };

    // 해당 날짜가 로딩 중인지 확인
    const isDateLoading = (date: string) => {
        const availability = availableDates.find(item => item.date === date);
        return availability?.isLoading || false;
    };

    // 달력에 표시할 날짜들 생성
    const generateCalendarDates = () => {
        const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
        const startCalendar = new Date(startOfMonth);
        startCalendar.setDate(startCalendar.getDate() - startOfMonth.getDay());

        const dates = [];
        const current = new Date(startCalendar);
        
        for (let i = 0; i < 42; i++) { // 6주 * 7일
            dates.push(new Date(current));
            current.setDate(current.getDate() + 1);
        }
        
        return dates;
    };

    const calendarDates = generateCalendarDates();
    
    // 이전/다음 달로 이동
    const goToPreviousMonth = () => {
        setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    };

    const goToNextMonth = () => {
        setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    };

    // 날짜 클릭 핸들러
    const handleDateClick = (date: Date) => {
        const dateString = format(date, 'yyyy-MM-dd');
        const isFuture = isBefore(new Date(), startOfDay(date)) && !isToday(date);
        
        if (!isFuture && isDateAvailable(dateString)) {
            onChange(dateString);
            setShowCalendar(false);
        }
    };

    // 날짜 스타일 계산
    const getDateStyle = (date: Date) => {
        const dateString = format(date, 'yyyy-MM-dd');
        const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
        const isFuture = isBefore(new Date(), startOfDay(date)) && !isToday(date);
        const isSelected = value === dateString;
        const hasData = isDateAvailable(dateString);
        const isLoading = isDateLoading(dateString);
        
        let baseStyle = 'w-8 h-8 text-sm rounded-full flex items-center justify-center cursor-pointer relative ';
        
        if (!isCurrentMonth) {
            baseStyle += 'text-gray-300 ';
        } else if (isFuture) {
            baseStyle += 'text-gray-300 cursor-not-allowed ';
        } else if (isSelected) {
            baseStyle += 'bg-blue-500 text-white ';
        } else if (isLoading) {
            baseStyle += 'text-gray-400 ';
        } else if (hasData) {
            baseStyle += 'text-black hover:bg-blue-100 ';
        } else {
            baseStyle += 'text-gray-400 cursor-not-allowed ';
        }
        
        return baseStyle;
    };

    // 달력 위치 계산
    const getCalendarPosition = () => {
        if (!buttonRef.current) return 'left-0';
        
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const calendarWidth = 280; // 달력의 최소 너비
        
        // 버튼의 오른쪽 끝에서 달력 너비만큼 더한 값이 화면을 벗어나는지 확인
        if (buttonRect.left + calendarWidth > windowWidth) {
            return 'right-0'; // 오른쪽 정렬
        }
        
        return 'left-0'; // 왼쪽 정렬 (기본)
    };

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                onClick={() => setShowCalendar(!showCalendar)}
                className="p-2 border rounded-lg text-sm bg-white min-w-[120px] text-left"
            >
                {value || t('locationHistory.calendar.selectDate')}
            </button>
            
            {showCalendar && (
                <div className={`absolute top-full ${getCalendarPosition()} mt-1 bg-white border rounded-lg shadow-lg z-50 p-4 min-w-[280px] max-w-[320px]`}>
                    {/* 달력 헤더 */}
                    <div className="flex justify-between items-center mb-4">
                        <button onClick={goToPreviousMonth} className="p-1 hover:bg-gray-100 rounded">
                            ←
                        </button>
                        <h3 className="font-medium">
                            {format(currentMonth, 'yyyy년 MM월')}
                        </h3>
                        <button onClick={goToNextMonth} className="p-1 hover:bg-gray-100 rounded">
                            →
                        </button>
                    </div>
                    
                    {/* 요일 헤더 */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                        {[
                            t('locationHistory.calendar.sunday'),
                            t('locationHistory.calendar.monday'),
                            t('locationHistory.calendar.tuesday'),
                            t('locationHistory.calendar.wednesday'),
                            t('locationHistory.calendar.thursday'),
                            t('locationHistory.calendar.friday'),
                            t('locationHistory.calendar.saturday')
                        ].map(day => (
                            <div key={day} className="text-center text-xs text-gray-500 py-1">
                                {day}
                            </div>
                        ))}
                    </div>
                    
                    {/* 날짜 그리드 */}
                    <div className="grid grid-cols-7 gap-1">
                        {calendarDates.map((date, index) => {
                            const dateString = format(date, 'yyyy-MM-dd');
                            const isLoading = isDateLoading(dateString);
                            
                            return (
                                <div
                                    key={index}
                                    className={getDateStyle(date)}
                                    onClick={() => handleDateClick(date)}
                                >
                                    {date.getDate()}
                                    {isLoading && (
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="w-1 h-1 bg-blue-500 rounded-full animate-pulse"></div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    
                    {/* 범례 */}
                    <div className="mt-3 pt-3 border-t text-xs">
                        <div className="flex justify-between">
                            <span className="text-black">● {t('locationHistory.calendar.hasData')}</span>
                            <span className="text-gray-400">● {t('locationHistory.calendar.noData')}</span>
                        </div>
                    </div>
                    
                    {isLoadingAvailability && (
                        <div className="mt-2 text-xs text-gray-500 text-center">
                            {t('locationHistory.calendar.checkingData')}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const LocationHistoryPage: React.FC<IProps> = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    
    // 오늘 날짜를 YYYY-MM-DD 형식으로 가져오는 함수
    const getTodayString = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };
    
    const selectedDate = searchParams.get('date') || getTodayString();
    
    const { data: users } = useQueryGetUsers();
    
    // 자동으로 첫 번째 사용자 선택
    const selectedUserId = useMemo(() => {
        if (users && users.length > 0) {
            return users[0].id;
        }
        return 0;
    }, [users]);

    const { data: selectedUser } = useQueryGetUserDetail(selectedUserId);
    const { data: dailyPositions, isLoading: isLoadingPositions, isError, error, refetch: refetchPositions } = 
        useQueryGetUserDailyPosition(selectedUserId, selectedDate);
    
    const [userInfo, setUserInfo] = useState<any>(null);
    const { isMount } = useIsMount();
    const [showDailyTrack, setShowDailyTrack] = useState<boolean>(true);
    const [positionsErrorMsg, setPositionsErrorMsg] = useState<string>(t('locationHistory.loading.userData'));
    const [selectedPosition, setSelectedPosition] = useState<IUserPosition | null>(null);
    
    // 위치 재생 관련 상태
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [currentPositionIndex, setCurrentPositionIndex] = useState<number>(0);
    const [playbackSpeed, setPlaybackSpeed] = useState<number>(1); // 재생 속도 (1x, 2x, 4x)
    const playbackTimerRef = useRef<number | null>(null);
    const [sortedPositions, setSortedPositions] = useState<IUserPosition[]>([]);

    // 날짜별 데이터 존재 여부 상태 및 캐시
    const [availableDates, setAvailableDates] = useState<IDateAvailability[]>([]);
    const [isLoadingAvailability, setIsLoadingAvailability] = useState<boolean>(false);
    const [currentCalendarMonth, setCurrentCalendarMonth] = useState<Date>(new Date());
    const dateCache = useRef<Map<string, boolean>>(new Map()); // 날짜별 데이터 존재 여부 캐시

    // 특정 월의 모든 날짜에 대해 데이터 존재 여부 확인
    const checkMonthDataAvailability = useCallback(async (month: Date, userId: number) => {
        if (!userId || userId <= 0) return;

        setIsLoadingAvailability(true);
        
        // 현재 달력에 표시되는 모든 날짜 생성 (이전달 말일 ~ 다음달 초일 포함)
        const startOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
        const startCalendar = new Date(startOfMonth);
        startCalendar.setDate(startCalendar.getDate() - startOfMonth.getDay());
        
        const datesToCheck = [];
        const current = new Date(startCalendar);
        
        for (let i = 0; i < 42; i++) { // 6주 * 7일
            const dateString = format(current, 'yyyy-MM-dd');
            
            // 캐시에서 확인하고 없는 경우만 요청 목록에 추가
            if (!dateCache.current.has(dateString)) {
                datesToCheck.push({
                    date: dateString,
                    hasData: false,
                    isLoading: true
                });
            }
            
            current.setDate(current.getDate() + 1);
        }
        
        // 캐시된 데이터와 새로 확인할 데이터 합치기
        const allDates: IDateAvailability[] = [];
        const currentForDisplay = new Date(startCalendar);
        
        for (let i = 0; i < 42; i++) {
            const dateString = format(currentForDisplay, 'yyyy-MM-dd');
            const cachedData = dateCache.current.get(dateString);
            
            if (cachedData !== undefined) {
                allDates.push({
                    date: dateString,
                    hasData: cachedData,
                    isLoading: false
                });
            } else {
                allDates.push({
                    date: dateString,
                    hasData: false,
                    isLoading: true
                });
            }
            
            currentForDisplay.setDate(currentForDisplay.getDate() + 1);
        }
        
        setAvailableDates(allDates);
        
        // 캐시되지 않은 날짜들만 API 호출
        if (datesToCheck.length === 0) {
            setIsLoadingAvailability(false);
            return;
        }
        
        // 각 날짜별로 데이터 존재 여부 확인 (병렬 처리)
        const promises = datesToCheck.map(async (dateInfo) => {
            try {
                const response = await apiGetUserDailyPosition(userId, dateInfo.date);
                const hasData = Array.isArray(response.data) && response.data.length > 0;
                
                // 캐시에 저장
                dateCache.current.set(dateInfo.date, hasData);
                
                return {
                    ...dateInfo,
                    hasData,
                    isLoading: false
                };
            } catch (error) {
                // 에러 발생시 false로 캐시
                dateCache.current.set(dateInfo.date, false);
                
                return {
                    ...dateInfo,
                    hasData: false,
                    isLoading: false
                };
            }
        });
        
        // 모든 요청이 완료되면 결과 업데이트
        try {
            await Promise.all(promises);
            
            // 최종 결과 생성 (캐시된 데이터 + 새로 확인한 데이터)
            const finalResults: IDateAvailability[] = [];
            const finalCurrent = new Date(startCalendar);
            
            for (let i = 0; i < 42; i++) {
                const dateString = format(finalCurrent, 'yyyy-MM-dd');
                const cachedData = dateCache.current.get(dateString);
                
                finalResults.push({
                    date: dateString,
                    hasData: cachedData || false,
                    isLoading: false
                });
                
                finalCurrent.setDate(finalCurrent.getDate() + 1);
            }
            
            setAvailableDates(finalResults);
        } catch (error) {
            console.error('날짜별 데이터 확인 중 오류:', error);
        } finally {
            setIsLoadingAvailability(false);
        }
    }, []);

    // 달력 월 변경 핸들러
    const handleMonthChange = useCallback((month: Date) => {
        setCurrentCalendarMonth(month);
        if (selectedUserId > 0) {
            checkMonthDataAvailability(month, selectedUserId);
        }
    }, [selectedUserId, checkMonthDataAvailability]);

    // 사용자 변경시 현재 달력 월의 데이터 다시 확인
    useEffect(() => {
        if (selectedUserId > 0) {
            checkMonthDataAvailability(currentCalendarMonth, selectedUserId);
        }
    }, [selectedUserId, currentCalendarMonth, checkMonthDataAvailability]);

    // 날짜 선택 핸들러
    const handleDateSelect = (date: string) => {
        const params = new URLSearchParams(searchParams);
        if (date) {
            params.set('date', date);
        } else {
            params.delete('date');
        }
        setSearchParams(params);
    };

    // 일일 위치 데이터 처리 및 정렬된 위치 데이터 설정
    useEffect(() => {
        if (!selectedUserId) {
            setPositionsErrorMsg(t('locationHistory.loading.userData'));
            setSortedPositions([]);
            return;
        }

        if (isLoadingPositions) {
            setPositionsErrorMsg('');
        } else if (isError) {
            setPositionsErrorMsg(t('locationHistory.error.dataLoadingError'));
        } else if (dailyPositions) {
            if (dailyPositions.length === 0) {
                setPositionsErrorMsg(t('locationHistory.error.noDataFound'));
                setSortedPositions([]);
            } else {
                setPositionsErrorMsg('');
                // 시간순으로 정렬된 위치 데이터 설정
                const sorted = [...dailyPositions].sort((a, b) => {
                    return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
                });
                setSortedPositions(sorted);
                // 재생 중단 및 인덱스 초기화
                setIsPlaying(false);
                setCurrentPositionIndex(0);
            }
        } else {
            setPositionsErrorMsg(t('locationHistory.error.noData'));
            setSortedPositions([]);
        }
    }, [dailyPositions, isLoadingPositions, isError, error, selectedUserId, t]);

    // 재생 속도에 따른 지연 시간 계산 (밀리초)
    const getDelayBySpeed = (speed: number): number => {
        switch (speed) {
            case 1: return 1000;  // 1x = 1초
            case 2: return 500;   // 2x = 0.5초
            case 4: return 250;   // 4x = 0.25초
            default: return 1000; // 기본 1초
        }
    };

    // 재생 시작 함수
    const startPlayback = () => {
        if (sortedPositions.length === 0) return;
        
        // 이미 재생 중이면 중지
        if (isPlaying) {
            stopPlayback();
            return;
        }
        
        // 재생 시작
        setIsPlaying(true);
        
        // 마지막 위치라면 처음으로 돌아가기
        if (currentPositionIndex >= sortedPositions.length - 1) {
            setCurrentPositionIndex(0);
        }
        
        // 타이머 시작 - useEffect에서 playNext가 호출됨
    };
    
    // 다음 위치로 이동하는 함수
    const playNext = useCallback(() => {
        if (playbackTimerRef.current) {
            window.clearTimeout(playbackTimerRef.current);
        }
        
        if (currentPositionIndex < sortedPositions.length - 1) {
            // 속도에 따른 고정 지연 시간 사용
            const delay = getDelayBySpeed(playbackSpeed);
            
            // 다음 위치로 이동
            playbackTimerRef.current = window.setTimeout(() => {
                setCurrentPositionIndex(prevIndex => prevIndex + 1);
                
                // 마지막 위치가 아니면 계속 재생
                // 내부 재귀 호출은 직접 playNext를 부르는 대신 useEffect에서 처리되도록 함
                // 여기서는 인덱스만 업데이트
            }, delay);
        } else {
            // 마지막 위치에 도달하면 재생 중단
            setIsPlaying(false);
        }
    }, [currentPositionIndex, sortedPositions.length, playbackSpeed]);
    
    // 재생 중일 때 playNext 재호출 로직 추가
    useEffect(() => {
        if (isPlaying && currentPositionIndex < sortedPositions.length - 1) {
            // 인덱스가 변경되었고 재생 중이면 다음 단계 호출
            // 단, 타이머가 이미 설정된 경우는 제외해야 중복 실행 방지 가능
            // 하지만 playNext 내부에서 clearTimeout을 하므로 괜찮음
            playNext();
        } else if (isPlaying && currentPositionIndex >= sortedPositions.length - 1) {
            setIsPlaying(false);
        }
    }, [currentPositionIndex, isPlaying, sortedPositions.length, playNext]);

    // 재생 상태가 변경될 때 타이머 업데이트
    useEffect(() => {
        if (!isPlaying && playbackTimerRef.current) {
            window.clearTimeout(playbackTimerRef.current);
            playbackTimerRef.current = null;
        } else if (isPlaying && !playbackTimerRef.current) {
             playNext();
        }
    }, [isPlaying, playNext]);
    
    // 재생 중지 함수
    const stopPlayback = () => {
        setIsPlaying(false);
        if (playbackTimerRef.current) {
            window.clearTimeout(playbackTimerRef.current);
            playbackTimerRef.current = null;
        }
    };
    
    // 재생 속도 변경 함수
    const changePlaybackSpeed = () => {
        // 1x -> 2x -> 4x -> 1x 순환
        setPlaybackSpeed(prevSpeed => {
            const newSpeed = prevSpeed === 4 ? 1 : prevSpeed * 2;
            
            // 재생 중이라면 useEffect에서 자동으로 속도 반영됨
            
            return newSpeed;
        });
    };
    
    // 컴포넌트 언마운트 시 타이머 정리
    useEffect(() => {
        return () => {
            if (playbackTimerRef.current) {
                window.clearTimeout(playbackTimerRef.current);
            }
        };
    }, []);

    const openUserInfo = (info?: IMarker) => {
        if (!info || userInfo?.id === info?.id) {
            setUserInfo(undefined);
        } else {
            const { latitude = 0, longitude = 0 } = info?.location || {};

            if (latitude === 0 && longitude === 0) {
                const infoWithoutAddress: IMarkerInfo = {
                    ...info!,
                    address: 'Coordinates not available',
                };

                setUserInfo(infoWithoutAddress);
                return;
            }

            const geocoder = new google.maps.Geocoder();
            geocoder.geocode(
                { location: { lat: latitude, lng: longitude } },
                (results, status) => {
                    if (status === 'OK' && results && results[0]) {
                        const infoWithAddress: IMarkerInfo = {
                            ...info!,
                            address: results[0].formatted_address,
                        };

                        setUserInfo(infoWithAddress);
                    } else {
                        const infoWithoutAddress: IMarkerInfo = {
                            ...info!,
                            address: 'Geocoding failed',
                        };

                        setUserInfo(infoWithoutAddress);
                    }
                }
            );
        }
    };

    // 위치 포인트 클릭 핸들러
    const handlePositionClick = (position: IUserPosition) => {
        setSelectedPosition(position);
    };

    // 시간 포매팅 함수
    const formatTimestamp = (timestamp: string) => {
        try {
            const date = new Date(timestamp);
            return date.toLocaleString('ko-KR', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit', 
                hour: '2-digit', 
                minute: '2-digit', 
                second: '2-digit',
                hour12: true 
            });
        } catch (error) {
            return timestamp;
        }
    };

    const getMarkerColor = (alertStatus: AlertStatus | undefined) => {
        let color = '#2966F4'; // 기본값
        switch (alertStatus) {
            case AlertStatus.EMERGENCY:
                color = '#FF003D';
                break;
            case AlertStatus.BIO:
                color = '#FF7D33';
                break;
            default:
                color = '#2966F4';
                break;
        }
        return color;
    };

    const marker = useMemo(() => {
        if (!selectedUser) return undefined;
        return {
            id: selectedUserId,
            deviceStatus: selectedUser?.device?.status,
            location: {
                latitude: selectedUser?.location?.latitude,
                longitude: selectedUser?.location?.longitude,
                accuracy: selectedUser?.location?.accuracy,
                timestamp: selectedUser?.location?.timestamp,
            },
            type: MarketTYPE.SINGLE,
            alertStatus: selectedUser?.alertStatus as AlertStatus ?? AlertStatus.NONE,
            users: [selectedUser],
        };
    }, [selectedUser, selectedUserId]);

    // 현재 재생 중인 위치
    const currentPlaybackPosition = useMemo(() => {
        return isPlaying && sortedPositions.length > 0 && currentPositionIndex < sortedPositions.length
            ? sortedPositions[currentPositionIndex]
            : null;
    }, [isPlaying, sortedPositions, currentPositionIndex]);

    const dailyPositionPath = useMemo(() => {
        if (sortedPositions.length === 0) return [];
        
        return sortedPositions.map(pos => ({
            lat: pos.latitude,
            lng: pos.longitude
        }));
    }, [sortedPositions]);

    // 위치 경로 스타일 
    const polylineOptions = {
        strokeColor: '#2966F4',
        strokeOpacity: 0.8,
        strokeWeight: 3,
    };

    // 지도 중심점 계산
    const mapCenter = useMemo(() => {
        if (sortedPositions.length > 0) {
            const firstPosition = sortedPositions[0];
            return { lat: firstPosition.latitude, lng: firstPosition.longitude };
        }
        if (selectedUser?.location) {
            return { lat: selectedUser.location.latitude || 0, lng: selectedUser.location.longitude || 0 };
        }
        // 서울 기본 좌표
        return { lat: 37.5665, lng: 126.9780 };
    }, [sortedPositions, selectedUser]);

    useEffect(() => {
        // 이미 렌더링된 컴포넌트에는 effect를 사용하지 않고 InfoWindowF 컴포넌트를 직접 사용합니다.
        // 기존 코드의 문제점: ReactDOMServer.renderToString으로 렌더링된 HTML 문자열을 InfoWindow의 content로 설정하면
        // React의 이벤트 핸들러나 라우팅 기능(Link)이 작동하지 않습니다.
        // 따라서 InfoWindowF 컴포넌트의 children으로 React 컴포넌트를 직접 렌더링하도록 변경합니다.
    }, [userInfo]);

    const mapOptions = {
        fullscreenControl: false
    };

    // 선택된 사용자 이름 가져기기
    const selectedUserName = useMemo(() => {
        return selectedUser?.name || '';
    }, [selectedUser]);

    return (
        <PageConfig showTopNav={true} showFooter={true}>
            <div className={'w-full h-full relative'}>
                {/* 상단 컨트롤 바 - 모바일 친화적 */}
                <div className="absolute top-0 left-0 right-0 bg-white shadow-md z-20 p-4">
                    <div className="flex flex-col gap-3">
                        {/* 첫 번째 줄: 사용자 정보 및 날짜 */}
                        <div className="flex gap-2 items-center">
                            <div className="flex-1 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                                <span className="text-sm text-gray-600">{t('locationHistory.user.label')}: </span>
                                <span className="font-medium">{selectedUserName || t('locationHistory.loading.loading')}</span>
                            </div>
                            <CustomDatePicker
                                value={selectedDate}
                                onChange={handleDateSelect}
                                availableDates={availableDates}
                                isLoadingAvailability={isLoadingAvailability}
                                onMonthChange={handleMonthChange}
                            />
                        </div>

                        {/* 두 번째 줄: 컨트롤 버튼들 */}
                        {selectedUserId > 0 && (
                            <div className="flex gap-2 flex-wrap">
                                <button 
                                    className="bg-blue-500 text-white px-3 py-1 rounded text-sm"
                                    onClick={() => refetchPositions()}
                                >
                                    {t('locationHistory.button.refresh')}
                                </button>
                                
                                {!isLoadingPositions && sortedPositions.length > 0 && (
                                    <>
                                        <button 
                                            className={`${isPlaying ? 'bg-red-500' : 'bg-green-500'} text-white px-3 py-1 rounded text-sm`}
                                            onClick={startPlayback}
                                        >
                                            {isPlaying ? t('locationHistory.button.stop') : t('locationHistory.button.play')}
                                        </button>
                                        
                                        <button
                                            className="bg-gray-200 px-3 py-1 rounded text-sm"
                                            onClick={changePlaybackSpeed}
                                        >
                                            {playbackSpeed}x
                                        </button>
                                        
                                        {currentPlaybackPosition && (
                                            <div className="bg-white px-3 py-1 rounded border text-sm">
                                                {currentPositionIndex + 1}/{sortedPositions.length}
                                            </div>
                                        )}
                                    </>
                                )}
                                
                                {!isLoadingPositions && dailyPositions && dailyPositions.length > 0 && (
                                    <button 
                                        className={`${showDailyTrack ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-700'} px-3 py-1 rounded text-sm`}
                                        onClick={() => setShowDailyTrack(!showDailyTrack)}
                                    >
                                        {showDailyTrack ? t('locationHistory.button.hidePath') : t('locationHistory.button.showPath')}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* 지도 영역 */}
                <div className="w-full h-full pt-32">
                    <Suspense fallback={null}>
                        {isMount && (
                            <div style={{ width: '100%', height: '100%' }}>
                                <GoogleMap
                                    center={mapCenter}
                                    zoom={15}
                                    mapContainerStyle={{ width: '100%', height: '100%' }}
                                    options={mapOptions}
                                >
                                    {isMount && selectedUser?.location && (
                                        <>
                                            <CircleF
                                                center={{ lat: selectedUser?.location?.latitude || 0, lng: selectedUser?.location?.longitude || 0 }}
                                                radius={selectedUser?.location.accuracy}
                                                options={{
                                                    fillColor: '#78bfffcc',
                                                    strokeColor: '#78bfffcc',
                                                    strokeWeight: 1,
                                                }}
                                            />
                                            <MarkerF
                                                position={{ lat: selectedUser?.location?.latitude || 0, lng: selectedUser?.location?.longitude || 0 }}
                                                icon={{
                                                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                                        `
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">
                                                            <circle cx="25" cy="25" r="20" fill="${getMarkerColor(selectedUser?.alertStatus)}" stroke="white" stroke-width="3"/>
                                                        </svg>
                                                        `
                                                    )}`,
                                                    scaledSize: new google.maps.Size(40, 40),
                                                }}
                                                onClick={() => openUserInfo(marker)}
                                            />

                                            {isMount && selectedUser?.addressLatitude && selectedUser?.addressLongitude && (
                                                <MarkerF
                                                    position={{ lat: selectedUser?.addressLatitude ?? 0, lng: selectedUser?.addressLongitude ?? 0 }}
                                                    icon={{
                                                        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                                            ReactDOMServer.renderToString(<HomeIcon />)
                                                        )}`,
                                                        scaledSize: new google.maps.Size(25, 25),
                                                    }}
                                                />
                                            )}

                                            {/* 하루 동안의 위치 경로 표시 */}
                                            {showDailyTrack && dailyPositionPath.length > 1 && (
                                                <PolylineF
                                                    path={dailyPositionPath}
                                                    options={polylineOptions}
                                                />
                                            )}

                                            {/* 위치 이동 경로의 각 포인트 표시 */}
                                            {showDailyTrack && !isPlaying && sortedPositions.map((pos, index) => (
                                                <MarkerF
                                                    key={`daily-pos-${index}`}
                                                    position={{ lat: pos.latitude, lng: pos.longitude }}
                                                    icon={{
                                                        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                                            `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                                                                <circle cx="8" cy="8" r="6" fill="#2966F4" stroke="white" stroke-width="2"/>
                                                            </svg>`
                                                        )}`,
                                                        scaledSize: new google.maps.Size(12, 12),
                                                    }}
                                                    opacity={0.8}
                                                    onClick={() => handlePositionClick(pos)}
                                                />
                                            ))}
                                            
                                            {/* 재생 중인 위치 마커 (크고 눈에 띄게) */}
                                            {currentPlaybackPosition && (
                                                <MarkerF
                                                    key="playback-marker"
                                                    position={{ lat: currentPlaybackPosition.latitude, lng: currentPlaybackPosition.longitude }}
                                                    icon={{
                                                        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                                            `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">
                                                                <circle cx="15" cy="15" r="10" fill="#FF5722" stroke="white" stroke-width="3"/>
                                                            </svg>`
                                                        )}`,
                                                        scaledSize: new google.maps.Size(30, 30),
                                                    }}
                                                    animation={google.maps.Animation.BOUNCE}
                                                />
                                            )}
                                        </>
                                    )}

                                    {userInfo && (
                                        <InfoWindowF
                                            position={{ lat: userInfo?.location?.latitude || 0, lng: userInfo?.location?.longitude || 0 }}
                                            onCloseClick={() => setUserInfo(undefined)}
                                        >
                                            <div style={{ fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 400 }}>
                                                <MarkerWindow info={userInfo} />
                                            </div>
                                        </InfoWindowF>
                                    )}

                                    {/* 선택된 위치 포인트 정보창 */}
                                    {selectedPosition && (
                                        <InfoWindowF
                                            position={{ lat: selectedPosition.latitude, lng: selectedPosition.longitude }}
                                            onCloseClick={() => setSelectedPosition(null)}
                                        >
                                            <div style={{ fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 400 }}>
                                                <p className="font-semibold mb-1">{t('locationHistory.info.locationInfo')}</p>
                                                <p className="text-sm">{t('locationHistory.info.time')}: {formatTimestamp(selectedPosition.timestamp)}</p>
                                                <p className="text-sm">{t('locationHistory.info.accuracy')}: {selectedPosition.accuracy}m</p>
                                                <p className="text-sm">{t('locationHistory.info.order')}: {selectedPosition.order}</p>
                                            </div>
                                        </InfoWindowF>
                                    )}
                                    
                                    {/* 현재 재생 중인 위치 정보창 */}
                                    {currentPlaybackPosition && !selectedPosition && (
                                        <InfoWindowF
                                            position={{ lat: currentPlaybackPosition.latitude, lng: currentPlaybackPosition.longitude }}
                                        >
                                            <div style={{ fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 400 }}>
                                                <p className="font-semibold mb-1">{t('locationHistory.info.currentLocation')}</p>
                                                <p className="text-sm">{t('locationHistory.info.time')}: {formatTimestamp(currentPlaybackPosition.timestamp)}</p>
                                                <p className="text-sm">{t('locationHistory.info.accuracy')}: {currentPlaybackPosition.accuracy}m</p>
                                                <p className="text-sm">{t('locationHistory.info.progress')}: {currentPositionIndex + 1}/{sortedPositions.length}</p>
                                            </div>
                                        </InfoWindowF>
                                    )}
                                </GoogleMap>
                            </div>
                        )}
                    </Suspense>
                </div>
                
                {/* 위치 데이터 상태 표시 */}
                {isLoadingPositions && selectedUserId > 0 && (
                    <div className="absolute bottom-32 left-1/2 transform -translate-x-1/2 bg-white p-2 rounded shadow z-10">
                        {t('locationHistory.loading.locationData')}
                    </div>
                )}
                
                {!isLoadingPositions && positionsErrorMsg && (
                    <div className="absolute bottom-32 left-1/2 transform -translate-x-1/2 bg-white p-3 rounded shadow z-10 flex flex-col items-center">
                        <div className="text-center">{positionsErrorMsg}</div>
                        {selectedUserId > 0 && (
                            <button 
                                className="mt-2 bg-blue-500 text-white px-3 py-1 rounded text-sm"
                                onClick={() => refetchPositions()}
                            >
                                {t('locationHistory.button.retry')}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </PageConfig>
    );
};

export default LocationHistoryPage;